import type { Pet } from "./pet";
import type { ConsumeEffect, Effect, RoundEffect } from "./effect";
import { Timing } from "./timing";
import { 属性 } from "./type";
import type { Type } from "./type";
import type { Seer } from "./seer";


/** 技能效果按声明顺序存放，并由每个 Effect 的 `timing` 决定生效阶段。 */
export type EffectGroup = Effect[];

/**
 * BattleBoard：封装技能定义与实际效果分组。
 */
export class BattleBoard {
    /**
     * （受到）伤害
     */
    damage?: number;
    /** 实际流失体力。 */
    hpLoss?: number;
    /**
     * （受到）固定伤害
     */
    fixedDamage?: number;
    /**
     * （受到）百分比伤害
     */
    percentDamage?: number;
    /**
     * 恢复体力
     */
    recoverHP?: number;
    /**
     * 未命中
     */
    notHit?: boolean;
    /**
     * 免疫受到的攻击
     */
    attackImmune?: boolean;
    /**
     * 重置战斗面板
     */
    reset() {
        this.damage = undefined;
        this.hpLoss = undefined;
        this.fixedDamage = undefined;
        this.percentDamage = undefined;
        this.recoverHP = undefined;
        this.notHit = undefined;
        this.attackImmune = undefined;
    }
}

/** 每种生命周期共用相同的时机索引，禁止分支伪时机进入注册表。 */
export class EffectRegistry<T> {
    /**
     * buckets，保存当前实例的buckets状态。
     */
    buckets: Partial<Record<Timing, T[]>> = {};

    /**
     * get：执行对应的领域操作。
     * @param timing 需要处理的战斗时机
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get(timing: Timing): T[] {
        if (timing === Timing.triggered || timing === Timing.untriggered) {
            throw new Error("子效果分支不能用作注册时机");
        }
        return (this.buckets[timing] ??= []);
    }

    /**
     * all：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    all(): T[] {
        return Object.values(this.buckets).flatMap((effects) => effects ?? []);
    }

    /**
     * retain：执行对应的领域操作。
     * @param predicate predicate参数，参与本次操作
     */
    retain(predicate: (effect: T) => boolean) {
        for (const timing of Object.keys(this.buckets) as Timing[]) {
            this.buckets[timing] = this.get(timing).filter(predicate);
        }
    }
}

/**
 * RoundEffectGroup：封装技能定义与实际效果分组。
 */
export class RoundEffectGroup extends EffectRegistry<RoundEffect> {}
/**
 * ConsumeEffectGroup：封装技能定义与实际效果分组。
 */
export class ConsumeEffectGroup extends EffectRegistry<ConsumeEffect> {}
/**
 * Skill：封装技能定义与实际效果分组。
 */
export class Skill {
    /**
     * 技能最大使用次数
     */
    maxPP: number;
    /**
     * 技能当前使用次数
     */
    curPP: number;

    /** 通用技能数据。攻击技能的威力与暴击值由 AttackSkill 提供。 */
    constructor(
        public name: string,
        public readonly type: Type,
        public description: string[],
        public effectGroup: EffectGroup,
        public preemptiveLevel: number = 0,
        public readonly sureHit: boolean = false,
        pp: number = 5,
    ) {
        this.maxPP = this.curPP = pp;
    }

    /**
     * 获取技能生效阶段为 timing 的效果
     * @param timing 生效阶段
     * @returns 效果组
     */
    getEffectsAt(timing: Timing): Effect[] {
        return this.effectGroup.filter((effect) => effect.timing === timing);
    }

    /**
     * 获取技能图标
     * @returns 图标url
     */
    getSkillTypeUrl() {
        return this.type.getImageSrc();
    }

    /** 非攻击技能没有基础伤害；攻击技能由 AttackSkill 覆盖。 */
    getBaseDamage(_sbjPet: Pet, _objPet: Pet, _sbjSeer: Seer, _objSeer: Seer): number | undefined {
        return undefined;
    }

    /**
     * onClick：执行对应的领域操作。
     */
    onClick() {
        console.debug(`技能 ${this.name} 被点击`);
    }
}

/** 攻击技能。暴击值是 16 分制的分子，而不是概率小数。 */
export abstract class AttackSkill extends Skill {
    /**
     * MIN_CRITICAL_STRIKE_VALUE，保存当前实例的MIN_CRITICAL_STRIKE_VALUE状态。
     */
    static readonly MIN_CRITICAL_STRIKE_VALUE = 0;
    /**
     * MAX_CRITICAL_STRIKE_VALUE，保存当前实例的MAX_CRITICAL_STRIKE_VALUE状态。
     */
    static readonly MAX_CRITICAL_STRIKE_VALUE = 16;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param name 名称
     * @param type type参数，参与本次操作
     * @param description description参数，参与本次操作
     * @param power 技能威力
     * @param effectGroup effectGroup参数，参与本次操作
     * @param preemptiveLevel preemptiveLevel参数，参与本次操作；默认值为 `0`
     * @param sureHit sureHit参数，参与本次操作；默认值为 `false`
     * @param criticalStrikeValue criticalStrikeValue参数，参与本次操作；默认值为 `1`
     * @param pp pp参数，参与本次操作；默认值为 `5`
     */
    constructor(
        name: string,
        type: Type,
        description: string[],
        public power: number,
        effectGroup: EffectGroup,
        preemptiveLevel: number = 0,
        sureHit: boolean = false,
        public readonly criticalStrikeValue: number = 1,
        pp: number = 5,
    ) {
        super(name, type, description, effectGroup, preemptiveLevel, sureHit, pp);
        if (
            !Number.isInteger(criticalStrikeValue) ||
            criticalStrikeValue < AttackSkill.MIN_CRITICAL_STRIKE_VALUE ||
            criticalStrikeValue > AttackSkill.MAX_CRITICAL_STRIKE_VALUE
        ) {
            throw new RangeError("攻击技能暴击值必须是 0 到 16 的整数");
        }
    }

    /** 子类选择物理或特殊攻击对应的能力值。 */
    protected abstract getAttackAndDefense(sbjPet: Pet, objPet: Pet): { /**
     * atk，保存当前实例的atk状态。
     */
    atk: number; /**
     * def，保存当前实例的def状态。
     */
    def: number };

    /**
     * 计算基础伤害
     * @param sbjPet
     * @param objPet
     * @param sbjSeer
     * @param objSeer
     * @returns
     */
    getBaseDamage(sbjPet: Pet, objPet: Pet, _sbjSeer: Seer, _objSeer: Seer) {
        /**
         * { atk, def }，供本模块相关规则使用。
         */
        const { atk, def } = this.getAttackAndDefense(sbjPet, objPet);

        // 判断是否为本系攻击
        /**
         * self，供本模块相关规则使用。
         */
        const self = this.type.isSelf(sbjPet.属性) ? 1.5 : 1;
        // 计算克制倍数
        /**
         * scale，供本模块相关规则使用。
         */
        const scale = this.type.getScale(objPet.属性);
        // 伤害浮动
        /**
         * random，供本模块相关规则使用。
         */
        const random = Math.round(_sbjSeer.random.next() * 38 + 217);
        // 技能变威力
        /**
         * power，供本模块相关规则使用。
         */
        const power = sbjPet.powerChange(this.power) * sbjPet.skillPowerMultiplier;
        // 造成伤害
        /**
         * damage，供本模块相关规则使用。
         */
        const damage = (Math.round((0.84 * power * atk) / def + 2) * self * scale * random) / 255;

        return damage;
    }
}

/** 物理攻击技能。 */
export class PhysicalAttackSkill extends AttackSkill {
    /**
     * getAttackAndDefense：执行对应的领域操作。
     * @param sbjPet 效果持有者精灵
     * @param objPet 效果作用目标精灵
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected getAttackAndDefense(sbjPet: Pet, objPet: Pet) {
        return {
            atk: sbjPet.getBattleAbilityValue(
                sbjPet.能力值.攻击,
                sbjPet.effectiveLevel("pAtk", objPet),
            ),
            def: objPet.getBattleAbilityValue(
                objPet.能力值.防御,
                objPet.effectiveLevel("pDef", sbjPet),
            ),
        };
    }
}

/** 特殊攻击技能。 */
export class SpecialAttackSkill extends AttackSkill {
    /**
     * getAttackAndDefense：执行对应的领域操作。
     * @param sbjPet 效果持有者精灵
     * @param objPet 效果作用目标精灵
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    protected getAttackAndDefense(sbjPet: Pet, objPet: Pet) {
        return {
            atk: sbjPet.getBattleAbilityValue(
                sbjPet.能力值.特攻,
                sbjPet.effectiveLevel("mAtk", objPet),
            ),
            def: objPet.getBattleAbilityValue(
                objPet.能力值.特防,
                objPet.effectiveLevel("mDef", sbjPet),
            ),
        };
    }
}

/** 属性技能。属性与命中率由类固定，属性技能没有威力和暴击值。 */
export class StatusSkill extends Skill {
    /**
     * type，保存当前实例的type状态。
     */
    static readonly type: typeof 属性 = 属性;
    /**
     * sureHit，保存当前实例的sureHit状态。
     */
    static readonly sureHit = true;

    /**
     * 创建实例并初始化所需的领域状态。
     * @param name 名称
     * @param description description参数，参与本次操作
     * @param effectGroup effectGroup参数，参与本次操作
     * @param preemptiveLevel preemptiveLevel参数，参与本次操作；默认值为 `0`
     * @param pp pp参数，参与本次操作；默认值为 `5`
     */
    constructor(
        name: string,
        description: string[],
        effectGroup: EffectGroup,
        preemptiveLevel: number = 0,
        pp: number = 5,
    ) {
        super(
            name,
            StatusSkill.type,
            description,
            effectGroup,
            preemptiveLevel,
            StatusSkill.sureHit,
            pp,
        );
    }
}
